import { useEffect } from 'react';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import MissingPage from '../../components/shared/errorMessages/MissingPage';
import TextHeader from '../../components/textHeader/TextHeader';
import MailingListSignup from '../../components/shared/mailing/Mailing';
import PageLayout from '../../components/shared/layout/PageLayout';
import { useQuery } from 'react-query';
import { getHomePageData } from '../../section/CMS/api/cmsApi';
import { captureAnalytics } from '../../utilities/Analytics';
import { CMSPageData } from '../../section/CMS/types/CMSTypes';

// Import new CMS components
import CMSConsumerCarouselBanner from '../../section/CMS/components/banner/CMSConsumerCarouselBanner';
import CMSConsumerItemGroupsGrid from '../../section/CMS/components/grid/CMSConsumerItemGroupsGrid';
import CMSConsumerNewArrivalsGrid from '../../section/CMS/components/grid/CMSConsumerNewArrivalsGrid';
import CMSConsumerFeaturedGallery from '../../section/CMS/components/gallery/CMSConsumerFeaturedGallery';
import CMSConsumerInternationalsSection from '../../section/CMS/components/internationals/CMSConsumerInternationalsSection';

function HomePage() {
    const {
        isError,
        isLoading,
        data: cmsResponse,
    } = useQuery(
      ['home-page-cms-consumer'],
      () => getHomePageData('live'),
      {
          staleTime: 600000, // Cache data for 10 minutes
      }
    );

    // Get the CMS data (always use live/published data for consumer site)
    const cmsData: CMSPageData | null = cmsResponse?.cms_page || null;

    // Get the children data (item groups)
    const childrenData = cmsData?.children_tile_data?.active || [];

    // Get the new arrivals data and filter published items only
    const rawNewArrivalsData = cmsData?.new_arrivals?.active || [];
    const newArrivalsData = rawNewArrivalsData
      .filter(item => item.is_published) // Only show published items for consumer
      .sort((a, b) => {
          const orderA = a.order_position || 999;
          const orderB = b.order_position || 999;
          return orderA - orderB;
      });

    useEffect(() => {
        // Scroll to top whenever the component is mounted
        window.scrollTo(0, 0);
        captureAnalytics('pageview', '/', 'Home');
    }, []);

    return (
      <>
          {isLoading ? (
            <LoadingScreen />
          ) : (
            <>
                {isError && <MissingPage />}

                {/* CMS Consumer Banner */}
                {cmsData && (
                  <CMSConsumerCarouselBanner
                    bannerMediaOption={cmsData.consumer_banner_media_option}
                    bannerMediaCarousel={cmsData.consumer_banner_media_carousel || []}
                    bannerMediaVideo={cmsData.consumer_banner_media_video}
                    bannerMediaVideoLink={cmsData.consumer_banner_media_video_link}
                    bannerLinkCategoryId={cmsData.consumer_banner_link_category_id}
                    bannerWidth={cmsData.consumer_banner_width}
                    bannerHeight={cmsData.consumer_banner_height}
                    viewMode="live"
                  />
                )}

                {/* Welcome Text Header */}
                <TextHeader
                  heading={'Welcome to Hertex'}
                  content={cmsData?.description ||
                    'Discover a world of inspiration through our premium fabric and wallpaper. At Hertex, we pride ourselves on offering expertly sourced collections that cater to diverse needs, from corporate and hospital settings to residential and contract applications. Whether you\'re looking for upholstery, curtaining or versatile multi-purpose fabrics, our contemporary designs ensure both quality and style.'}
                  isCentered={false}
                  width="70%"
                  marginTop={10}
                  marginBottom={5}
                />

                {/* Item Groups Grid */}
                <CMSConsumerItemGroupsGrid
                  childrenData={childrenData}
                  hasTitles={false}
                  hasButtons={false}
                  hasBackgroundText={true}
                  spacingValue={6}
                  hasCollectionLinks={false}
                  marginTop={5}
                  marginBottom={5}
                />

                {/* New Arrivals - Only show if has data */}
                {newArrivalsData.length > 0 && (
                  <CMSConsumerNewArrivalsGrid
                    newArrivalsData={newArrivalsData}
                    header="New arrivals"
                    hasTitles={true}
                    hasButtons={true}
                    hasBackgroundText={false}
                    spacingValue={4}
                    marginTop={'5rem'}
                    marginBottom={'5rem'}
                    viewMode="live"
                  />
                )}

                {/* Featured Gallery - Only show if enabled and has data */}
                {cmsData?.featured_collection_display_switch && cmsData?.featured_collection && (
                  <CMSConsumerFeaturedGallery
                    featuredCollection={cmsData.featured_collection}
                    hasVideo={false}
                    invert={false}
                    marginTop={5}
                    marginBottom={5}
                    viewMode="live"
                  />
                )}

                {/* Internationals - Only show if has data */}
                {cmsData?.internationals_media_single && (
                  <CMSConsumerInternationalsSection
                    internationalsSingle={cmsData.internationals_media_single}
                    internationalsLink={cmsData.internationals_media_single_link}
                    internationalsDescription={cmsData.internationals_description}
                    marginTop={15}
                    marginBottom={15}
                  />
                )}

                {/* Mailing List Signup */}
                <MailingListSignup
                  isColor={true}
                  marginBottom={5}
                />
            </>
          )}
      </>
    );
}

export default HomePage;